import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {DocumentListSourceInfo} from '../../api/types/document.type';
import {useDocumentViewerListId} from '../../hooks/document/useDocument';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../redux/slice/docViewSlice';
import {SourceTypeCode} from '../../shared/constants';
import {clearDocViewState} from '../document-viewer/docViewerUtils';
import {createDocumentViewerListRequestPayloadForDocView} from './DocListUtils';

interface IDocListActionItem {
  documentListMasterID: number;
  actionId: string;
  isAllPages: boolean;
  isChildrens: boolean;
  sourceDetails: never;
  updatedColumns: {
    FieldID: number | string;
    FieldName: string;
    FieldLabel: string;
    FieldType: string;
    SequenceNumber: number;
    FieldDataTye: string;
  }[];
  totalRecords?: number;
  docsToExclude: {sourceID: number | string}[];
  objSourceOverrides?: Partial<DocumentListSourceInfo>;
}

interface UseDocListActionForDocViewReturn {
  isDocViewActionLoading: boolean;
  onDocViewAction: (item: IDocListActionItem) => void;
}

const useDocListActionForDocView = (): UseDocListActionForDocViewReturn => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {mutate: fetchDocumentViewerListId, isPending: isDocViewActionLoading} =
    useDocumentViewerListId();

  const onDocViewAction = useCallback(
    ({
      actionId,
      documentListMasterID,
      isAllPages,
      isChildrens,
      sourceDetails,
      updatedColumns,
      totalRecords,
      docsToExclude,
      objSourceOverrides = {},
    }: IDocListActionItem) => {
      const payload = createDocumentViewerListRequestPayloadForDocView({
        isInverse: isAllPages,
        isAllChildrenSelected: isAllPages && isChildrens ? true : false,
        documentListMasterID,
        sourceDetails: isAllPages ? [] : sourceDetails,
        docsToExclude: docsToExclude,
        columnFilters: updatedColumns,
        totalDocumentToView: isAllPages ? (totalRecords ?? 0) : 0,
        objSourceOverrides,
      });

      fetchDocumentViewerListId(payload, {
        onSuccess: (response) => {
          clearDocViewState();
          const masterId =
            response.documentListID ?? response.defaultMasterID ?? 0;
          dispatch(storeDocumentListMasterId(String(masterId)));
          dispatch(
            storeSourceTypeCode(
              objSourceOverrides?.sourceTypeCode ?? SourceTypeCode.DOCUMENT
            )
          );
          if (actionId === 'ViewInDocView') {
            navigate('/DocumentViewer/DocView');
          } else if (actionId === 'ViewInDocViewInNewTab') {
            const newWindow = window.open('about:blank', '_blank');
            if (newWindow) {
              newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`;
            }
          }
        },
        onError: (error) => {
          console.warn('handleViewInDocView error', error);
        },
      });
    },
    [dispatch, fetchDocumentViewerListId, navigate]
  );

  return {
    isDocViewActionLoading,
    onDocViewAction,
  };
};

export {useDocListActionForDocView};
